import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { useLiff } from './LiffContext.js';
import { resolveContext } from '../api/contextClient.js';
import type { ResolveContextResponse } from '@pos/shared/schemas';
import { shouldUseMockLiff } from '../hooks/useMockLiff.js';

interface ContextResolutionContextValue {
  isReady: boolean;
  status: 'bound' | 'unbound' | 'loading' | 'error' | 'not_in_group';
  boundTenant: ResolveContextResponse['tenant'] | null;
  unboundGroup: ResolveContextResponse['group'] | null;
  error: Error | null;
  lineGroupId: string | null;
  resolve: () => Promise<void>;
}

const ContextResolutionContext = createContext<
  ContextResolutionContextValue | undefined
>(undefined);

/**
 * Context Resolution Provider
 * 處理 LIFF 群組上下文解析，優先使用 LIFF Group Context，其次使用 URL Query
 */
export function ContextResolutionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { isReady: liffReady, liff, shouldUseLiff } = useLiff();
  const [isReady, setIsReady] = useState(false);
  const [status, setStatus] = useState<
    'bound' | 'unbound' | 'loading' | 'error' | 'not_in_group'
  >('loading');
  const [boundTenant, setBoundTenant] = useState<
    ResolveContextResponse['tenant'] | null
  >(null);
  const [unboundGroup, setUnboundGroup] = useState<
    ResolveContextResponse['group'] | null
  >(null);
  const [error, setError] = useState<Error | null>(null);
  const [lineGroupId, setLineGroupId] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const hasResolvedRef = useRef(false);

  // 解析上下文的函數
  const resolve = async () => {
    if (hasResolvedRef.current) {
      console.log('[ContextResolution] 已經解析過，跳過');
      return;
    }

    const useMock = shouldUseMockLiff();

    try {
      // 只從 URL 取得 groupId（不從 context 取得）
      const { getGroupIdFromUrl } = await import('../utils/getGroupIdFromUrl');
      let groupId = getGroupIdFromUrl();

      if (groupId) {
        console.log('[ContextResolution] ✅ 從 URL 取得 groupId:', groupId);
      }

      // 在 Mock 模式下，如果還是沒有 groupId，嘗試從 localStorage 取得
      if (useMock && !groupId) {
        const mockGroupId = localStorage.getItem('mock_line_group_id');
        if (mockGroupId) {
          groupId = mockGroupId;
          console.log(
            '[ContextResolution] 從 localStorage 取得 Mock groupId:',
            groupId
          );
        }
      }

      // 如果還是沒有 groupId，無法解析
      if (!groupId) {
        console.log('[ContextResolution] 無法取得 groupId，無法解析上下文');
        setStatus('not_in_group');
        setIsReady(true);
        return;
      }

      setLineGroupId(groupId);
      setStatus('loading');

      // 呼叫 API 解析綁定狀態
      const result = await resolveContext(groupId);

      if (!isMountedRef.current) return;

      if (result.status === 'bound') {
        setStatus('bound');
        setBoundTenant(result.tenant || null);
        setUnboundGroup(null);
        setError(null);
      } else {
        setStatus('unbound');
        setUnboundGroup(result.group || null);
        setBoundTenant(null);
        setError(null);
      }

      hasResolvedRef.current = true;
    } catch (err) {
      if (!isMountedRef.current) return;

      console.error('[ContextResolution] 解析失敗:', err);
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setStatus('error');
    } finally {
      if (isMountedRef.current) {
        setIsReady(true);
      }
    }
  };

  // 當 LIFF 準備好時，執行解析
  useEffect(() => {
    if (!isMountedRef.current) return;

    // 如果不需要 LIFF，直接嘗試從 URL Query 解析
    if (!shouldUseLiff) {
      resolve();
      return;
    }

    // 如果需要 LIFF，等待 LIFF 準備好
    if (liffReady && liff) {
      resolve();
    }
  }, [liffReady, liff, shouldUseLiff]);

  // 清理
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const value: ContextResolutionContextValue = {
    isReady,
    status,
    boundTenant,
    unboundGroup,
    error,
    lineGroupId,
    resolve,
  };

  return (
    <ContextResolutionContext.Provider value={value}>
      {children}
    </ContextResolutionContext.Provider>
  );
}

/**
 * Hook to use Context Resolution context
 */
export function useContextResolution(): ContextResolutionContextValue {
  const context = useContext(ContextResolutionContext);
  if (context === undefined) {
    throw new Error(
      'useContextResolution must be used within a ContextResolutionProvider'
    );
  }
  return context;
}
